import { TableHeader } from 'components/organisms/table';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  ITableSortLabel,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableSortLabel
} from '@sanlam-glacier/app-common/components';
import { caseTrackingTableHeaderData } from 'modules/case-tracking/utils/case-tracking-table.function';
import { CaseTrackingTableRow } from './CaseTrackingTableRow';
import styles from './case-tracking.module.scss';
import { MobileCaseTrackingCards } from '../../molecules/mobile-case-tracking-cards/MobileCaseTrackingCards';
import { AdvancedSearch } from '../../atoms/case-tracking-advanced-search/CaseTrackingAdvancedSearch';
import { CaseType } from 'modules/case-tracking/types/case-tracking.types';
import { CaseFieldsEnum, Stage } from 'modules/case-tracking/types/case-tracking.enums';
import TableFilter from '../table-filter/TableFilter';
import { InstructionCategory, Status } from 'modules/case-tracking/types/filtering/filtering.enums';
import NoResults from '../../molecules/no-results/NoResults';
import clsx from 'clsx';
import { ScreenSize, useScreenSize } from 'modules/case-tracking/hooks/useScreenSize';
import { mockCaseData } from 'stores/case-tracking-mock-data';

export const CaseTrackingTable = () => {
  const [order, setOrder] = useState<ITableSortLabel['direction']>(TableSortLabel.DIRECTION.ASC);
  const [orderBy, setOrderBy] = useState<keyof CaseType>('created');

  const [searchResults, setSearchResults] = useState<CaseType[]>([]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');

  const [instructionCategories, setInstructionCategories] = useState<InstructionCategory[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [status, setStatus] = useState<Status[]>([]);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const data = mockCaseData;
  const screenSize = useScreenSize();

  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  useEffect(() => {
    if (searchResults.length === 0 && !currentSearchTerm) {
      setSearchResults(data);
    }
  }, [data]);

  const filteredData = useMemo(() => {
    let results = searchResults;

    if (instructionCategories.length > 0) {
      results = results.filter((item) => instructionCategories.includes(item.instructionCategory));
    }

    if (stages.length > 0) {
      results = results.filter((item) => stages.includes(item.stageDescription));
    }

    if (status.length > 0) {
      results = results.filter((item) => status.includes(item.status as Status));
    }

    if (dateRange[0]) {
      const startDate = dateRange[0];
      const endDate = dateRange[1] || startDate;

      results = results.filter((item) => {
        const itemDate = new Date(item.created);
        itemDate.setHours(0, 0, 0, 0);

        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    return results;
  }, [searchResults, instructionCategories, stages, status, dateRange]);

  const handleDateRangeUpdate = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    setPage(0);
  };

  //Source of truth
  const sortedData = useMemo(() => {
    const dataToSort = [...filteredData];

    return dataToSort.sort((a: CaseType, b: CaseType) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];

      if (orderBy === CaseFieldsEnum.clientName) {
        valueA = a.clientName;
        valueB = b.clientName;
      }

      // Date sorting
      if (valueA instanceof Date && valueB instanceof Date) {
        return order === TableSortLabel.DIRECTION.DESC
          ? valueA.getTime() - valueB.getTime()
          : valueB.getTime() - valueA.getTime();
      }

      // String sorting
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === TableSortLabel.DIRECTION.DESC
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      // Number sorting
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === TableSortLabel.DIRECTION.DESC ? valueA - valueB : valueB - valueA;
      }

      return 0;
    });
  }, [filteredData, order, orderBy]);

  const paginatedData = useMemo(() => {
    return sortedData.slice(page * pageSize, page * pageSize + pageSize);
  }, [sortedData, page, pageSize]);

  const handleCategoriesUpdate = (categories: InstructionCategory[]) => {
    setInstructionCategories(categories);
    setPage(0);
  };

  const handleStagesUpdate = (stages: Stage[]) => {
    setStages(stages);
    setPage(0);
  };

  const handleStatusUpdate = (status: Status[]) => {
    setStatus(status);
    setPage(0);
  };

  const handleSearchResults = useCallback((results: CaseType[], searchTerm: string) => {
    setSearchResults(results);
    setCurrentSearchTerm(searchTerm);
    setPage(0);
  }, []);

  const handleSort = (propertyName: keyof CaseType | undefined) => {
    if (!propertyName || propertyName === 'stage') return;

    const newOrder =
      propertyName === orderBy
        ? order === TableSortLabel.DIRECTION.ASC
          ? TableSortLabel.DIRECTION.DESC
          : TableSortLabel.DIRECTION.ASC
        : TableSortLabel.DIRECTION.DESC;

    setOrder(newOrder);
    setOrderBy(propertyName);
  };

  //Needed so we don't "break" the main component :D
  useEffect(() => {
    setTimeout(() => {
      if (!tableHeaderRef.current) return;

      const headerCells =
        tableHeaderRef.current.querySelectorAll<HTMLElement>('.inner-header-cell');

      headerCells.forEach((cell, index) => {
        const key = caseTrackingTableHeaderData[index]?.key;
        if (key) {
          cell.setAttribute('data-testid', `headers${key}`);
        }
      });
    }, 2000);
  }, []);

  return (
    <div className={styles.caseTrackingTableContainer}>
      {screenSize !== ScreenSize.Desktop ? (
        <div className={clsx([paginatedData.length <= pageSize ? styles.noPagination : ''])}>
          <div className={styles.mobileContainer}>
            <AdvancedSearch
              data={data}
              onSearchResults={handleSearchResults}
              placeholder='Search instructions...'
              className={styles.searchInput}
            />

            <div>
              <TableFilter
                handleCategoriesUpdate={handleCategoriesUpdate}
                handleStagesUpdate={handleStagesUpdate}
                handleStatusUpdate={handleStatusUpdate}
                downloadDisabled={sortedData.length === 0}
                handleDateRangeUpdate={handleDateRangeUpdate}
                shouldShowClear={
                  !(screenSize === ScreenSize.Tablet_md || screenSize === ScreenSize.Tablet_sm)
                }
              />
            </div>
          </div>
          {sortedData.length !== 0 ? (
            <div className={styles.mobileCardsContainer}>
              <MobileCaseTrackingCards
                cases={sortedData}
                isTablet={
                  screenSize === ScreenSize.Tablet_md || screenSize === ScreenSize.Tablet_sm
                }
              />
            </div>
          ) : (
            <div className={styles.mobileErrorContainer}>
              <NoResults
                categoryFilters={instructionCategories}
                stageFilters={stages}
                statusFilters={status}
                searchTerm={currentSearchTerm}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={styles.filterContainer}>
            <div className={styles.caseTrackingTable}>
              <AdvancedSearch
                data={data}
                onSearchResults={handleSearchResults}
                placeholder='Search instructions...'
                className={styles.searchInput}
              />

              <TableFilter
                handleCategoriesUpdate={handleCategoriesUpdate}
                handleStagesUpdate={handleStagesUpdate}
                handleStatusUpdate={handleStatusUpdate}
                handleDateRangeUpdate={handleDateRangeUpdate}
                downloadDisabled={sortedData.length === 0}
                shouldShowClear={
                  screenSize === ScreenSize.Desktop || screenSize === ScreenSize.Mobile
                }
              />
            </div>
          </div>
          {sortedData.length !== 0 ? (
            <div className={styles.caseTrackingTable}>
              <Table data-testid={'case-tracking-table'}>
                <TableHeader
                  data-testid={'headers'}
                  headers={caseTrackingTableHeaderData}
                  orderBy={orderBy}
                  align={TableCell.ALIGN.LEFT}
                  direction={order}
                  handleSort={(propertyName) => handleSort(propertyName as keyof CaseType)}
                  tableHeaderClassName={styles.tableSortHeaderLabel}
                />
                <TableBody>
                  {paginatedData.map((row) => (
                    <CaseTrackingTableRow key={row.id} row={row} />
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                className={styles.tableFooter}
                count={sortedData.length}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(newPage: number) => setPage(newPage)}
                onRowsPerPageChange={(newPageSize: number) => {
                  setPage(0);
                  setPageSize(newPageSize);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                closeMenuOnSelection
              />
            </div>
          ) : (
            <div className={styles.errorContainer}>
              <NoResults
                categoryFilters={instructionCategories}
                stageFilters={stages}
                statusFilters={status}
                searchTerm={currentSearchTerm}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
